import { TagColor as Color } from '@factorialco/deprecated-design-system'
import type { StatusVariant } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { FilterState } from 'modules/shift_management/hooks/reducers/filter'
import type { WEEK_STATUSES } from 'modules/shift_management/hooks/reducers/schedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

type WeekStatus = keyof typeof WEEK_STATUSES

const statusColor = (status: WeekStatus) => {
  const STATUS_TAG_TO_COLOR: Record<WeekStatus, Color> = {
    draft: Color.champagne,
    published: Color.malibu,
    conflict: Color.radical,
  }

  return STATUS_TAG_TO_COLOR[status] || STATUS_TAG_TO_COLOR['draft']
}

const statusTag = (status: WeekStatus, conflictsCount: number) => {
  const STATUS_TAG: Record<WeekStatus, string> = {
    draft: i18n.t('shift_management.publish.week_status.draft'),
    published: i18n.t('shift_management.publish.week_status.published'),
    conflict: i18n.t('shift_management.publish.week_status.conflict', {
      count: conflictsCount,
    }),
  }

  return STATUS_TAG[status] || STATUS_TAG['draft']
}

const statusLabel = (status: WeekStatus, conflictsCount: number) => {
  const STATUS_LABEL: Record<WeekStatus, string> = {
    draft: i18n.t('shift_management.publish.week_status.draft_tooltip'),
    published: i18n.t('shift_management.publish.week_status.published_tooltip'),
    conflict: i18n.t('shift_management.publish.week_status.conflict_tooltip', {
      count: conflictsCount,
    }),
  }

  return STATUS_LABEL[status] || STATUS_LABEL['draft']
}

const statusVariant = (status: WeekStatus): StatusVariant => {
  const STATUS_VARIANT: Record<WeekStatus, StatusVariant> = {
    draft: 'warning',
    published: 'positive',
    conflict: 'critical',
  }

  return STATUS_VARIANT[status] || STATUS_VARIANT['draft']
}

export default function getStatusTagData({
  shiftsRows,
  conflictsCount,
  hasDrafts,
  filters,
  treatConflictsAsDraft = false,
}: {
  shiftsRows: ShiftsRow[]
  conflictsCount: number
  hasDrafts: boolean
  filters: FilterState
  treatConflictsAsDraft?: boolean
}) {
  let status: WeekStatus

  const hasPublishedShifts = shiftsRows
    .flatMap((row) => row.shifts.shifts)
    .some((shift) => shift.isPublished)

  const filterByWithoutConflict = (filters.withoutShiftIds?.length ?? 0) > 0

  if (!filterByWithoutConflict && conflictsCount > 0) {
    status = treatConflictsAsDraft ? 'draft' : 'conflict'
  }
  else if (!hasPublishedShifts || hasDrafts) status = 'draft'
  else status = 'published'

  return {
    status,
    statusTag: statusTag(status, conflictsCount),
    statusType: statusColor(status),
    statusLabel: statusLabel(status, conflictsCount),
    statusVariant: statusVariant(status),
  }
}
